import {Component, Input, OnInit, ViewChildren} from '@angular/core';
import {
  AdditionalCoveragesFilterOption,
  CoveragesFilterOption,
  FilterKey,
  FilterMessage,
  FilterMessageEvent,
  FilterOption,
  FilterType,
  OffersFilter
} from './offers-filter.model';
import {FormGroup} from '@angular/forms';
import {OffersFilterConfig} from './offers-filter.config';
import {FieldConfig} from '../../../../../models/field-config';
import {FormConfig} from '../../../../../models/form-config';
import {FormFactoryService} from '../../../../../services/form-factory.service';
import {FiltersService} from '../../../../../services/filters.service';
import {GoogleAnalyticsEventsService} from '../../../../../services/google-analytics-events.service';
import {TranslateService} from '@ngx-translate/core';
import {GA4EventKey} from '../../../../../../core/consts/ga4-event-key.enum';
import {CheckboxComponent} from '../../../../../components/fields/checkbox/checkbox.component';

@Component({
  selector: 'app-offers-filter',
  templateUrl: './offers-filter.component.html',
  styleUrls: ['./offers-filter.component.scss']
})
export class OffersFilterComponent implements OnInit {

  @Input() filter: OffersFilter;
  @Input() offersCountEnabled: boolean;
  @ViewChildren(CheckboxComponent) checkboxes;

  formGroup: FormGroup;
  formConfig: FormConfig;
  formFactoryService: FormFactoryService;
  bean: any;

  constructor(private filtersService: FiltersService,
              private ts: TranslateService,
              private googleAnalyticsService: GoogleAnalyticsEventsService) {
    this.filtersService.resetFiltersEvent.subscribe(() => this.reset());
    this.filtersService.offersCountUpdateEvent.subscribe(() => this.updateOffersCount());
  }

  get anyOption(): FilterOption {
    return this.filter.options.find(option => option.isAnyOption);
  }

  ngOnInit() {
    this.initBean();
    this.createFormConfig();
    this.buildForm();
    this.initControlChanges();
    this.handleDisabledAndSelectedOptions();
    this.handleFilterMessages();
    this.updateOffersCount();
  }

  getFieldConfig(key: string): FieldConfig {
    return this.formConfig.fieldsConfig.find(fieldConfig => fieldConfig.fieldKey === key);
  }

  onCheckboxClick(option: FilterOption) {
    setTimeout(() => {
      const value = this.formGroup.controls[option.key].value;
      if (value) {
        this.sendOfferFilterClickEvent(option);
      }
      this.filtersService.detailCoverageFilterClick.emit({
        filterKey: this.filter.key,
        optionKey: option.key,
        checked: value
      });
    }, 0);
  }

  private initBean(): void {
    this.bean = {};
    const appliedOptions = this.filtersService.filtersApplied ? this.filtersService.confirmedFilters[this.filter.key] : this.filter.initialOptions;
    if (appliedOptions) {
      appliedOptions.forEach(option => {
        if (this.filter.type === FilterType.CHECKBOX) {
          this.bean[option] = true;
        } else if (this.filter.type === FilterType.RADIO) {
          this.bean[this.filter.key] = option;
        }
      });
    }
  }

  private buildForm() {
    this.formGroup = new FormGroup({});
    this.formFactoryService = new FormFactoryService(this.formConfig, this.bean);
    this.formFactoryService.buildFormGroup(this.formGroup);
  }

  private createFormConfig() {
    const config = new OffersFilterConfig(this.filter);
    this.formConfig = config.createFormConfig();
  }

  private initControlChanges() {
    Object.keys(this.formGroup.controls).forEach(controlName => {
      this.formGroup.controls[controlName].valueChanges.subscribe(value => {
        setTimeout(() => this.updateFilter(controlName, value), 0);
      });
    });
  }

  private handleDisabledAndSelectedOptions(): void {
    this.filter.options.forEach(option => {
      if (option.selectedAndDisabled) {
        if (this.filter.type === FilterType.CHECKBOX) {
          this.formGroup.controls[option.key].disable({emitEvent: false});
        }
      }
    });
  }

  private updateFilter(controlName: string, value: any): void {
    const optionKey = this.filter.type === FilterType.CHECKBOX ? controlName : value;
    if (this.filter.type === FilterType.RADIO) {
      this.filtersService.addFilter(this.filter, optionKey);
    } else {
      value ? this.filtersService.addFilter(this.filter, optionKey) : this.filtersService.removeFilter(this.filter, optionKey);
      if (this.filtersService.getOption(this.filter, optionKey).isAnyOption) {
        this.clearSelectedOptionsInCheckboxFilter();
      } else {
        if (this.anyOption) {
          this.formGroup.controls[this.anyOption.key].reset(false, { emitEvent: false })
        }
      }
    }
    this.handleSpecialConditions(controlName, value);
  }

  private handleSpecialConditions(controlName: string, value: any): void {
    if (this.filter.key === FilterKey.ADDITIONAL_COVERAGE && controlName === AdditionalCoveragesFilterOption.ASSISTANCE && value === false) {
      this.filtersService.filterMessageEvent.emit({ targets: [FilterKey.ASSISTANCE_CONDITIONS], message: FilterMessage.CLEAR_ASSISTANCE_CONDITIONS });
    }
    if (this.filter.key === FilterKey.COVERAGE && controlName === CoveragesFilterOption.OC_AC) {
      if (!value) {
        this.filtersService.filterMessageEvent.emit({
          targets: this.filtersService.acConcernedFilters,
          message: FilterMessage.CLEAR_AC_CONDITIONS
        });
      } else {
        this.filtersService.ocAcSelectedByUser = true;
      }
    }
    if (this.filtersService.acConcernedFilters.includes(this.filter.key) && value === true) {
      this.filtersService.filterMessageEvent.emit({ targets: [FilterKey.COVERAGE], message: FilterMessage.SELECT_OC_AC });
    }
  }

  private clearSelectedOptionsInCheckboxFilter(emitEvent: boolean = false): void {
    Object.keys(this.formGroup.controls).forEach(controlName => {
      if (!this.filtersService.getOption(this.filter, controlName).isAnyOption) {
        if (this.formGroup.controls[controlName].value) {
          this.formGroup.controls[controlName].reset(false, { emitEvent: emitEvent });
        }
      }
    });
  }

  private selectAllFilterOptions(emitEvent: boolean = false): void {
    Object.keys(this.formGroup.controls).forEach(controlName => {
      this.formGroup.controls[controlName].setValue(true, { emitEvent: emitEvent });
    });
  }

  private reset(emitControlChange: boolean = false): void {
    if (this.filter.type === FilterType.CHECKBOX) {
      this.clearSelectedOptionsInCheckboxFilter(emitControlChange);
    }
    if (this.filter.initialOptions) {
      this.filter.initialOptions.forEach(option => {
        if (this.filter.type === FilterType.CHECKBOX) {
          this.formGroup.controls[option].setValue(true, {emitEvent: false});
        } else if (this.filter.type === FilterType.RADIO) {
          this.formGroup.controls[this.filter.key].setValue(option, {emitEvent: false});
        }
      });
    }
  }

  private handleFilterMessages(): void {
    this.filtersService.filterMessageEvent.subscribe((messageData: FilterMessageEvent) => {
      if (messageData.targets.includes(this.filter.key)) {
        switch (messageData.message) {
          case FilterMessage.CLEAR_ASSISTANCE_CONDITIONS: {
            this.clearSelectedOptionsInCheckboxFilter(true);
            break;
          }
          case FilterMessage.CLEAR_AC_CONDITIONS: {
            this.clearSelectedOptionsInCheckboxFilter(true);
            break;
          }
          case FilterMessage.SELECT_OC_AC: {
            if (!this.formGroup.controls[CoveragesFilterOption.OC_AC].value) {
              this.filtersService.ocAcSelectedByUser = false;
              this.formGroup.controls[CoveragesFilterOption.OC_AC].setValue(true, {emitEvent: false});
              this.filtersService.addFilter(this.filter, CoveragesFilterOption.OC_AC);
            }
            break;
          }
          case FilterMessage.SELECT_ALL_FILTER_OPTIONS: {
            this.selectAllFilterOptions(true);
            break;
          }
          case FilterMessage.RESET_ALL_FILTER_OPTIONS: {
            this.reset(true);
            break;
          }
          // case FilterMessage.REMOVE_OC_AC: {
          //   if (!this.filtersService.ocAcSelectedByUser) {
          //     this.formGroup.controls[CoveragesFilterOption.OC_AC].setValue(false);
          //   }
          //   break;
          // }
        }
      }
    });
  }

  private sendOfferFilterClickEvent(option: FilterOption): void {
    const filterName = this.ts.instant(this.filter.title);
    const optionName = this.ts.instant(option.label);

    this.googleAnalyticsService.send(GA4EventKey.OFFER_FILTER_CLICK, {
      offer_filter_name: filterName,
      offer_filter_option: optionName
    });
  }

  private updateOffersCount(): void {
    if (this.offersCountEnabled) {
      this.filter.options.forEach(option => {
        this.getFieldConfig(option.key).captionCount = this.filtersService.getOfferCount(this.filter.key, option.key);
      });
    }
  }

}
